/**
 * The per-project sections of a project page — payment plan, unit types and
 * sizes, floor plans, nearby places, amenities, gallery — built from the
 * project's detail record, in every site language.
 *
 * These are what make one project page different from the next. Before them
 * a project page was a template with a name and a price swapped in, and
 * Google grouped hundreds of them as duplicates of each other.
 *
 * Shared by the React page and the prerenderer, so the static HTML and the
 * hydrated page list the same facts in the same words. Plain JS.
 */

import { normaliseProject, paymentPlan, planSentence, projectPlace } from './projectSeo.js';
import { fullPrice, parseHandover } from './seoCopy.js';
import { NAP } from './nap.js';

const L = {
    ar: {
        plan: 'خطة السداد', units: 'أنواع الوحدات والمساحات', floorPlans: 'المخططات', nearby: 'الموقع وما حوله',
        amenities: 'المرافق', gallery: 'معرض الصور', description: 'وصف المطوّر',
        booking: 'عند الحجز', construction: 'خلال الإنشاء', handover: 'عند التسليم',
        post: (m) => (m ? `بعد التسليم (على ${m} شهراً)` : 'بعد التسليم'),
        unit: 'الوحدة', size: 'المساحة', price: 'السعر', distance: 'المسافة', from: 'من',
        studio: 'استوديو', beds: (n) => (n === 1 ? 'غرفة نوم واحدة' : n === 2 ? 'غرفتا نوم' : n <= 10 ? `${n} غرف نوم` : `${n} غرفة نوم`),
        sqft: 'قدم مربع', km: 'كم', floorPlan: (t, u) => `مخطط ${u} في ${t}`,
        kinds: { feature: 'الواجهة', exterior: 'منظر خارجي', interior: 'تصميم داخلي', lobby: 'الردهة', general: 'المخطط العام', other: 'التصميم المعماري' }
    },
    en: {
        plan: 'Payment plan', units: 'Unit types and sizes', floorPlans: 'Floor plans', nearby: 'Location and nearby',
        amenities: 'Amenities', gallery: 'Gallery', description: "Developer's description",
        booking: 'On booking', construction: 'During construction', handover: 'On handover',
        post: (m) => (m ? `After handover (over ${m} months)` : 'After handover'),
        unit: 'Unit', size: 'Size', price: 'Price', distance: 'Distance', from: 'from',
        studio: 'Studio', beds: (n) => `${n} bedroom${n === 1 ? '' : 's'}`,
        sqft: 'sq ft', km: 'km', floorPlan: (t, u) => `${t} ${u} floor plan`,
        kinds: { feature: 'facade', exterior: 'exterior view', interior: 'interior', lobby: 'lobby', general: 'masterplan', other: 'architecture' }
    },
    ru: {
        plan: 'План оплаты', units: 'Типы юнитов и площади', floorPlans: 'Планировки', nearby: 'Расположение и окрестности',
        amenities: 'Инфраструктура', gallery: 'Галерея', description: 'Описание от застройщика',
        booking: 'При бронировании', construction: 'Во время строительства', handover: 'При сдаче',
        post: (m) => (m ? `После сдачи (${m} мес.)` : 'После сдачи'),
        unit: 'Юнит', size: 'Площадь', price: 'Цена', distance: 'Расстояние', from: 'от',
        studio: 'Студия', beds: (n) => `${n} ${n % 10 === 1 && n % 100 !== 11 ? 'спальня' : [2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100) ? 'спальни' : 'спален'}`,
        sqft: 'кв. футов', km: 'км', floorPlan: (t, u) => `${t}: планировка, ${u.toLowerCase()}`,
        kinds: { feature: 'фасад', exterior: 'вид снаружи', interior: 'интерьер', lobby: 'лобби', general: 'генплан', other: 'архитектура' }
    },
    zh: {
        plan: '付款计划', units: '户型与面积', floorPlans: '户型图', nearby: '位置与周边',
        amenities: '配套设施', gallery: '图集', description: '开发商介绍',
        booking: '预订时', construction: '建设期间', handover: '交付时',
        post: (m) => (m ? `交付后（${m}个月内）` : '交付后'),
        unit: '户型', size: '面积', price: '价格', distance: '距离', from: '起',
        studio: '开间', beds: (n) => `${n}居室`,
        sqft: '平方英尺', km: '公里', floorPlan: (t, u) => `${t}${u}户型图`,
        kinds: { feature: '外立面', exterior: '外景', interior: '室内', lobby: '大堂', general: '总体规划', other: '建筑设计' }
    },
    ur: {
        plan: 'ادائیگی پلان', units: 'یونٹ کی اقسام اور رقبہ', floorPlans: 'فلور پلانز', nearby: 'مقام اور اردگرد',
        amenities: 'سہولیات', gallery: 'تصاویر', description: 'ڈیولپر کی تفصیل',
        booking: 'بکنگ پر', construction: 'تعمیر کے دوران', handover: 'حوالگی پر',
        post: (m) => (m ? `حوالگی کے بعد (${m} ماہ میں)` : 'حوالگی کے بعد'),
        unit: 'یونٹ', size: 'رقبہ', price: 'قیمت', distance: 'فاصلہ', from: 'سے',
        studio: 'اسٹوڈیو', beds: (n) => `${n} بیڈروم`,
        sqft: 'مربع فٹ', km: 'کلومیٹر', floorPlan: (t, u) => `${t} — ${u} فلور پلان`,
        kinds: { feature: 'بیرونی منظر', exterior: 'بیرونی منظر', interior: 'اندرونی ڈیزائن', lobby: 'لابی', general: 'ماسٹر پلان', other: 'آرکیٹیکچر' }
    }
};
export const contentLabels = (lang) => L[lang] || L.en;

const num = (v) => {
    const n = parseFloat(String(v ?? '').replace(/,/g, ''));
    return isFinite(n) && n > 0 ? n : 0;
};
/** "450–540 sq ft": one unit after the range, not one per bound. */
const sizeRange = (a, b, unit) => (a && b && b > a ? `${sqft(a)}–${sqft(b)} ${unit}` : a ? `${sqft(a)} ${unit}` : '');
const sqft = (n) => Math.round(n).toLocaleString('en-US');

/** "0" → "Studio", "2" → "2 bedrooms", in the page language. */
export function bedroomLabel(bedroom, lang) {
    const T = contentLabels(lang);
    const n = Number(String(bedroom).trim());
    if (String(bedroom).trim() === '0' || /studio/i.test(String(bedroom))) return T.studio;
    return isFinite(n) && n > 0 ? T.beds(n) : String(bedroom);
}

/** Payment plan rows: [{ label, percent }], booking first. */
export function planRows(raw, lang) {
    const plan = paymentPlan(raw);
    if (!plan) return [];
    const T = contentLabels(lang);
    return [
        plan.booking && { key: 'booking', label: T.booking, percent: plan.booking },
        plan.construction && { key: 'construction', label: T.construction, percent: plan.construction },
        plan.handover && { key: 'handover', label: T.handover, percent: plan.handover },
        plan.post && { key: 'post', label: T.post(plan.postMonths), percent: plan.post }
    ].filter(Boolean);
}

/** Unit types from typical_units: [{ label, type, size, price }]. */
export function unitRows(raw, lang) {
    const T = contentLabels(lang);
    const units = Array.isArray(raw.typical_units) ? raw.typical_units : [];
    const seen = new Set();
    return units
        .map((u) => {
            const size = sizeRange(num(u.lowest_area), num(u.highest_area), T.sqft);
            const lo = num(u.lowest_price), hi = num(u.highest_price);
            return {
                bedroom: String(u.bedroom ?? ''),
                label: bedroomLabel(u.bedroom, lang),
                type: u.property_types || '',
                size,
                price: lo ? `${hi > lo ? `${fullPrice(lo, lang)} – ${fullPrice(hi, lang)}` : fullPrice(lo, lang)}` : ''
            };
        })
        .filter((u) => {
            const key = `${u.label}|${u.type}|${u.size}|${u.price}`;
            if (seen.has(key) || (!u.size && !u.price)) return false;
            seen.add(key);
            return true;
        })
        .sort((a, b) => (Number(a.bedroom) || 0) - (Number(b.bedroom) || 0));
}

/** Floor plan images, one per unit type: [{ src, alt }]. */
export function floorPlans(raw, lang) {
    const p = normaliseProject(raw);
    const T = contentLabels(lang);
    const units = Array.isArray(raw.typical_units) ? raw.typical_units : [];
    const out = [];
    const seen = new Set();
    for (const u of units) {
        for (const src of Array.isArray(u.images) ? u.images : []) {
            if (!src || seen.has(src)) continue;
            seen.add(src);
            out.push({ src, alt: T.floorPlan(p.title, bedroomLabel(u.bedroom, lang)) });
        }
    }
    return out.slice(0, 8);
}

/** Nearby places with distances: [{ name, distance }]. */
export function nearbyPlaces(raw, lang) {
    const T = contentLabels(lang);
    const list = Array.isArray(raw.nearby_locations) ? raw.nearby_locations : [];
    return list
        .map((n) => {
            const km = parseFloat(String(n.distance || '').replace(',', '.'));
            const shown = isFinite(km) ? (lang === 'ru' ? String(km).replace('.', ',') : String(km)) : '';
            return { name: String(n.name || '').trim(), distance: shown ? `${shown} ${T.km}` : '' };
        })
        .filter((n) => n.name)
        .slice(0, 8);
}

/** Amenity names, de-duplicated. */
export function amenities(raw) {
    const names = [
        ...(raw.amenities_and_features?.features_names || []),
        ...(Array.isArray(raw.facilities) ? raw.facilities.map((f) => f?.name) : [])
    ].map((s) => String(s || '').trim()).filter(Boolean);
    return [...new Set(names)].slice(0, 16);
}

/**
 * The gallery with descriptive alt text: "1WOOD — interior 2" instead of
 * "Gallery 2". Images are taken from the detail record's categories when
 * present, so the alt can say what each one shows.
 */
export function gallery(raw, lang) {
    const p = normaliseProject(raw);
    const T = contentLabels(lang);
    const out = [];
    const seen = new Set();
    const push = (src, kind, i) => {
        if (!src || typeof src !== 'string' || seen.has(src)) return;
        seen.add(src);
        out.push({ src, alt: `${p.title} — ${T.kinds[kind] || T.kinds.exterior}${i ? ` ${i}` : ''}` });
    };
    const imgs = raw.images && typeof raw.images === 'object' && !Array.isArray(raw.images) ? raw.images : null;
    if (imgs) {
        push(imgs.feature, 'feature', 0);
        for (const kind of ['exterior', 'interior', 'lobby', 'other', 'general']) {
            (Array.isArray(imgs[kind]) ? imgs[kind] : []).forEach((src, i) => push(src, kind, i + 1));
        }
    }
    (Array.isArray(raw.all_images) ? raw.all_images : []).forEach((src, i) => push(src, 'exterior', i + 1));
    if (!out.length && p.image) push(p.image, 'feature', 0);
    return out.slice(0, 12);
}

/**
 * The project's lead image: the hero at the top of its page, and the page's
 * LCP element. One definition for the static HTML and the React page, so both
 * request the same file.
 */
export function heroImage(raw, lang) {
    const p = normaliseProject(raw);
    const src = p.image || (Array.isArray(raw.all_images) && raw.all_images[0]) || '';
    if (!src || typeof src !== 'string') return null;
    // Not the feed's own alt text: that is English, and on the other language
    // versions it would be the one untranslated label on the page.
    return { src, alt: `${p.title} — ${contentLabels(lang).kinds.feature}` };
}

/** The developer's own description, as safe plain-text paragraphs. */
export function descriptionParagraphs(raw) {
    const html = String(raw.description || '');
    if (!html.trim()) return [];
    return html
        .replace(/<\s*br\s*\/?>/gi, '\n')
        .replace(/<\/(p|div|li|h\d)>/gi, '\n')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .split(/\n+/)
        .map((s) => s.replace(/\s+/g, ' ').trim())
        .filter((s) => s.length > 2)
        .slice(0, 20);
}

/** Everything at once, for the page components. */
export function projectContent(raw, lang) {
    return {
        labels: contentLabels(lang),
        plan: planRows(raw, lang),
        units: unitRows(raw, lang),
        floorPlans: floorPlans(raw, lang),
        nearby: nearbyPlaces(raw, lang),
        amenities: amenities(raw),
        gallery: gallery(raw, lang),
        description: descriptionParagraphs(raw)
    };
}

/**
 * Per-project FAQ — the visible block and the FAQPage schema. Every answer
 * comes from this project's own record (price, plan, units, nearby places,
 * handover), so no two project pages share an FAQ.
 */
export function projectFaq(raw, lang = 'ar') {
    const p = normaliseProject(raw);
    const brand = NAP.name[lang] || NAP.name.en;
    const phone = NAP.phoneDisplay;
    const lic = NAP.dldBrokerNumber;
    const price = fullPrice(p.priceMin, lang);
    const ho = parseHandover(p.handover);
    const handover = ho ? ho.label[lang] : null;
    const plan = p.soldOut ? null : planSentence(paymentPlan(raw), lang);
    const place = projectPlace(raw, lang);
    const units = unitRows(raw, lang).slice(0, 6);
    const near = nearbyPlaces(raw, lang).slice(0, 3);
    const t = p.title;
    const by = (fmt) => (p.developer ? fmt(p.developer) : '');

    const unitList = (sep, fmt) => units.map((u) => fmt(u.label, u.size)).join(sep);
    const nearList = (sep, fmt) => near.map((n) => fmt(n.name, n.distance)).join(sep);

    const Q = {
        ar: {
            price: [`كم سعر ${t}؟`, price
                ? (p.soldOut ? `كانت أسعار ${t} تبدأ من ${price}، والوحدات مباعة بالكامل حالياً. نرشّح لك بدائل في المنطقة نفسها بسعر المطوّر.` : `تبدأ أسعار ${t} من ${price} بسعر المطوّر مباشرة وبدون عمولة على المشتري عبر ${brand}.`)
                : `أسعار ${t} تتغيّر حسب توفّر الوحدات. تواصل معنا على ${phone} للحصول على قائمة الأسعار المحدّثة من المطوّر.`],
            plan: plan && [`ما خطة السداد في ${t}؟`, `${plan} تُضاف رسوم تسجيل دائرة الأراضي 4% عند الحجز، ونؤكّد الخطة السارية مع المطوّر قبل الحجز.`],
            units: units.length && [`ما أنواع الوحدات ومساحاتها في ${t}؟`, `${unitList('؛ ', (l, sz) => `${l}${sz ? ` بمساحة ${sz}` : ''}`)}.`],
            where: [`أين يقع ${t}؟`, `يقع ${t}${by((d) => ` من تطوير ${d}`)} في ${place}، الإمارات العربية المتحدة.${near.length ? ` وبالقرب منه: ${nearList('، ', (n, d) => `${n}${d ? ` (${d})` : ''}`)}.` : ''}`],
            handover: [`متى تسليم ${t}؟`, handover
                ? (ho.past ? `اكتمل تسليم ${t} في ${handover}.` : `التسليم المتوقّع لمشروع ${t} هو ${handover} حسب جدول المطوّر المعلن.`)
                : `لم يُعلن المطوّر بعد موعد تسليم نهائياً لمشروع ${t}. تواصل معنا لآخر تحديث.`],
            buy: [`كيف أشتري وحدة في ${t}؟`, `تواصل مع ${brand} على ${phone} — وسيط مرخّص من دائرة الأراضي والأملاك برقم ${lic}. نحجز الوحدة بسعر المطوّر ونسجّل العقد باسمك.`]
        },
        en: {
            price: [`How much does ${t} cost?`, price
                ? (p.soldOut ? `${t} started from ${price} and is currently sold out. We can suggest alternatives in the same area at the developer price.` : `${t} starts from ${price} at the direct developer price, with zero buyer commission through ${brand}.`)
                : `${t} pricing changes with availability. Call us on ${phone} for the current developer price list.`],
            plan: plan && [`What is the payment plan for ${t}?`, `${plan} The 4% DLD registration fee is paid at booking, and we confirm the current plan with the developer before you reserve.`],
            units: units.length && [`What unit types and sizes are available in ${t}?`, `${unitList('; ', (l, sz) => `${l}${sz ? `: ${sz}` : ''}`)}.`],
            where: [`Where is ${t} located?`, `${t}${by((d) => ` by ${d}`)} is in ${place}, United Arab Emirates.${near.length ? ` Nearby: ${nearList(', ', (n, d) => `${n}${d ? ` (${d})` : ''}`)}.` : ''}`],
            handover: [`When is ${t} handover?`, handover
                ? (ho.past ? `${t} was handed over in ${handover}.` : `${t} has an expected handover of ${handover}, per the developer's published schedule.`)
                : `The developer has not yet confirmed a final handover date for ${t}. Contact us for the latest update.`],
            buy: [`How do I buy a unit in ${t}?`, `Contact ${brand} on ${phone} — a Dubai Land Department licensed broker, No. ${lic}. We reserve the unit at the developer price and register the contract in your name.`]
        },
        ru: {
            price: [`Сколько стоит ${t}?`, price
                ? (p.soldOut ? `Цены на юниты ${t} начинались от ${price}; сейчас всё продано. Подберём альтернативы в том же районе по цене застройщика.` : `Цены на юниты ${t} начинаются от ${price} — цена застройщика без комиссии для покупателя через ${brand}.`)
                : `Цены на юниты ${t} зависят от наличия. Позвоните нам: ${phone}, чтобы получить актуальный прайс застройщика.`],
            plan: plan && [`Какой план оплаты у ${t}?`, `${plan} Сбор DLD 4% оплачивается при бронировании; актуальный план мы подтверждаем у застройщика перед бронью.`],
            units: units.length && [`Какие юниты и площади есть в ${t}?`, `${unitList('; ', (l, sz) => `${l}${sz ? `: ${sz}` : ''}`)}.`],
            where: [`Где находится ${t}?`, `Расположение ${t}${by((d) => ` (застройщик ${d})`)}: ${place}, ОАЭ.${near.length ? ` Рядом: ${nearList(', ', (n, d) => `${n}${d ? ` (${d})` : ''}`)}.` : ''}`],
            handover: [`Когда сдача ${t}?`, handover
                ? (ho.past ? `${t} сдан в ${handover}.` : `Ожидаемая сдача ${t} — ${handover}, по графику застройщика.`)
                : `Застройщик ещё не объявил окончательную дату сдачи ${t}. Свяжитесь с нами для актуальной информации.`],
            buy: [`Как купить юнит ${t}?`, `Свяжитесь с ${brand}: ${phone} — брокер, лицензированный Земельным департаментом Дубая, № ${lic}. Мы бронируем юнит по цене застройщика и регистрируем договор на ваше имя.`]
        },
        zh: {
            price: [`${t}的价格是多少？`, price
                ? (p.soldOut ? `${t}曾从${price}起，目前已售罄。我们可按开发商价格推荐同区域的替代项目。` : `${t}起价${price}，通过${brand}按开发商直售价购买，买家零佣金。`)
                : `${t}的价格随房源供应变化。请致电${phone}获取开发商最新价目表。`],
            plan: plan && [`${t}的付款计划是怎样的？`, `${plan}预订时需支付4%的迪拜土地局注册费，预订前我们会向开发商确认当前付款计划。`],
            units: units.length && [`${t}有哪些户型和面积？`, `${unitList('；', (l, sz) => `${l}${sz ? `：${sz}` : ''}`)}。`],
            where: [`${t}位于哪里？`, `${t}${by((d) => `（开发商：${d}）`)}位于阿联酋${place}。${near.length ? `周边：${nearList('、', (n, d) => `${n}${d ? `（${d}）` : ''}`)}。` : ''}`],
            handover: [`${t}何时交付？`, handover
                ? (ho.past ? `${t}已于${handover}交付。` : `根据开发商公布的时间表，${t}预计${handover}交付。`)
                : `开发商尚未公布${t}的最终交付日期，请联系我们了解最新进展。`],
            buy: [`如何购买${t}的房源？`, `请致电${phone}联系${brand}（迪拜土地局持牌经纪，编号${lic}）。我们按开发商价格为您预订，并以您的名义登记合同。`]
        },
        ur: {
            price: [`${t} کی قیمت کیا ہے؟`, price
                ? (p.soldOut ? `${t} کی قیمت ${price} سے شروع ہوتی تھی اور فی الحال تمام یونٹس فروخت ہو چکے ہیں۔ ہم اسی علاقے میں ڈیولپر قیمت پر متبادل تجویز کر سکتے ہیں۔` : `${t} کی قیمت ${price} سے شروع ہوتی ہے — ${brand} کے ذریعے براہِ راست ڈیولپر قیمت پر، کوئی کمیشن نہیں۔`)
                : `${t} کی قیمتیں دستیابی کے ساتھ بدلتی ہیں۔ تازہ قیمتوں کے لیے ${phone} پر رابطہ کریں۔`],
            plan: plan && [`${t} کا ادائیگی پلان کیا ہے؟`, `${plan} DLD رجسٹریشن فیس 4% بکنگ پر ادا ہوتی ہے، اور بکنگ سے پہلے ہم ڈیولپر سے موجودہ پلان کی تصدیق کرتے ہیں۔`],
            units: units.length && [`${t} میں کون سے یونٹس اور رقبے دستیاب ہیں؟`, `${unitList('؛ ', (l, sz) => `${l}${sz ? `: ${sz}` : ''}`)}۔`],
            where: [`${t} کہاں واقع ہے؟`, `${t}${by((d) => ` (ڈیولپر ${d})`)} ${place}، متحدہ عرب امارات میں واقع ہے۔${near.length ? ` قریب: ${nearList('، ', (n, d) => `${n}${d ? ` (${d})` : ''}`)}۔` : ''}`],
            handover: [`${t} کی حوالگی کب ہے؟`, handover
                ? (ho.past ? `${t} کی حوالگی ${handover} میں ہو چکی۔` : `ڈیولپر کے شیڈول کے مطابق ${t} کی متوقع حوالگی ${handover} ہے۔`)
                : `ڈیولپر نے ابھی ${t} کی حتمی حوالگی کی تاریخ کا اعلان نہیں کیا۔ تازہ معلومات کے لیے رابطہ کریں۔`],
            buy: [`${t} میں یونٹ کیسے خریدیں؟`, `${brand} سے ${phone} پر رابطہ کریں — دبئی لینڈ ڈیپارٹمنٹ سے لائسنس یافتہ بروکر، نمبر ${lic}۔ ہم ڈیولپر قیمت پر یونٹ بک کرتے ہیں اور معاہدہ آپ کے نام رجسٹر کرتے ہیں۔`]
        }
    }[lang] || {};

    return ['price', 'plan', 'units', 'where', 'handover', 'buy']
        .map((k) => Q[k])
        .filter(Boolean)
        .map(([q, a]) => ({ q, a }));
}
